import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Result,
  Card,
  Button,
  Alert,
  Typography,
  Space,
  Tag,
} from 'antd';
import {
  BankOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { BankTransferInstructions } from '../../components/subscription/BankTransferInstructions';
import {
  ENTERPRISE_PREMIUM_ENABLED,
  getSubscriptionPlanConfig,
} from '@shared/config/subscription-plans';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';
import { BANK_TRANSFER_STORAGE_KEY } from '../../components/company/bankTransferConstants';

const { Title, Text } = Typography;

interface BankTransferInstructions {
  bankName: string;
  branchName: string;
  accountNumber: string;
  accountName: string;
  amount: number;
  currency: string;
  dueDate?: string;
  accountType?: string;
}

interface BankTransferSubscriptionResult {
  subscriptionId: string;
  customerId: string;
  invoiceId: string;
  invoiceUrl: string | null;
  bankTransferInstructions: BankTransferInstructions;
  status: string;
  nextInvoiceDate?: string;
}

interface BankTransferStatePayload {
  result: BankTransferSubscriptionResult;
  planCode?: string;
  companyName?: string;
  savedAt?: string;
}

const SUBSCRIPTION_PLAN_LABELS = {
  starter: getSubscriptionPlanConfig('starter').label,
  standard: getSubscriptionPlanConfig('standard').label,
  professional: getSubscriptionPlanConfig('professional').label,
  enterprise: getSubscriptionPlanConfig('enterprise').label,
  enterprise_premium: getSubscriptionPlanConfig('enterprise_premium').label,
} satisfies Record<ManagedPlanCode, string>;

const isManagedPlanCode = (value: string): value is ManagedPlanCode => {
  return value in SUBSCRIPTION_PLAN_LABELS;
};

const resolvePlanLabel = (code: string): string => {
  if (code === 'enterprise_premium' && !ENTERPRISE_PREMIUM_ENABLED) {
    return '提供停止プラン（エンタープライズ・プレミアム）';
  }
  if (isManagedPlanCode(code)) {
    return SUBSCRIPTION_PLAN_LABELS[code];
  }
  return `不明なプラン (${code})`;
};

function formatCurrency(amount: number, currency: string) {
  if (!Number.isFinite(amount)) {
    return '-';
  }
  const formatter = new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
}

function formatDate(value?: string) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const BankTransferConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<BankTransferStatePayload | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');

  useEffect(() => {
    let resolved: BankTransferStatePayload | null = null;
    const statePayload = (location.state ?? null) as BankTransferStatePayload | null;
    if (statePayload?.result) {
      resolved = statePayload;
    } else {
      try {
        const storedRaw = sessionStorage.getItem(BANK_TRANSFER_STORAGE_KEY);
        if (storedRaw) {
          const stored = JSON.parse(storedRaw) as BankTransferStatePayload;
          if (stored?.result) {
            resolved = stored;
          }
        }
      } catch (error) {
        console.warn('[BankTransferConfirmationPage] Failed to restore state', error);
        setLoadState('error');
        return;
      }
    }

    if (!resolved) {
      setLoadState('empty');
      return;
    }

    setPayload(resolved);
    setLoadState('ready');
  }, [location.state]);

  const planLabel = useMemo(() => {
    if (!payload?.planCode) {
      return '不明なプラン';
    }
    return resolvePlanLabel(payload.planCode);
  }, [payload?.planCode]);

  const instructions = payload?.result.bankTransferInstructions;
  const formattedAmount = instructions
    ? formatCurrency(instructions.amount, instructions.currency)
    : '-';
  const dueDate = instructions?.dueDate ? formatDate(instructions.dueDate) : '-';

  const handleBackToSettings = () => {
    navigate('/app/subscription');
  };

  if (loadState === 'loading') {
    return (
      <div className="container mx-auto px-4 py-12">
        <Card loading style={{ maxWidth: 720, margin: '0 auto' }} />
      </div>
    );
  }

  if (loadState === 'empty' || !payload) {
    return (
      <div className="container mx-auto px-4 py-12">
        <Result
          status="warning"
          title="振込先情報が見つかりませんでした"
          subTitle="申し込みが完了していないか、ページの有効期限が切れた可能性があります。"
          extra={
            <Button type="primary" onClick={handleBackToSettings} icon={<ArrowLeftOutlined />}>
              契約プラン設定に戻る
            </Button>
          }
        />
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="container mx-auto px-4 py-12">
        <Result
          status="error"
          title="振込先情報の復元に失敗しました"
          subTitle="ブラウザを再読み込みするか、もう一度申し込み手続きを実施してください。"
          extra={
            <Button type="primary" onClick={handleBackToSettings} icon={<ArrowLeftOutlined />}>
              契約プラン設定に戻る
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 920, margin: '0 auto' }}>
        <Result
          icon={<BankOutlined />}
          status="success"
          title="お申し込みありがとうございます"
          subTitle="以下の振込先にお支払いください。入金確認後、契約が有効化されます。"
        />

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={4} style={{ marginBottom: 12 }}>
                お申し込み内容
              </Title>
              <Space direction="vertical" size="small">
                <Text>
                  <Text strong>会社名:</Text> {payload.companyName || '---'}
                </Text>
                <Text>
                  <Text strong>プラン:</Text> {planLabel}
                </Text>
                <Text>
                  <Text strong>請求額:</Text> {formattedAmount}
                </Text>
                <Text>
                  <Text strong>支払期限:</Text> {dueDate}
                </Text>
                <Text>
                  <Text strong>請求書番号:</Text> {payload.result.invoiceId}
                </Text>
                <Tag color="orange">入金待ち ({payload.result.status})</Tag>
                {payload.result.nextInvoiceDate && (
                  <Text type="secondary">
                    次回請求予定日: {formatDate(payload.result.nextInvoiceDate)}
                  </Text>
                )}
              </Space>
            </div>

            <div>
              <Title level={4} style={{ marginBottom: 12 }}>
                振込先情報
              </Title>
              <BankTransferInstructions
                instructions={{
                  bankName: instructions?.bankName,
                  branchName: instructions?.branchName,
                  accountType: instructions?.accountType,
                  accountNumber: instructions?.accountNumber,
                  accountName: instructions?.accountName,
                  amount: formattedAmount,
                  paymentDueDate: dueDate,
                  invoiceUrl: payload.result.invoiceUrl,
                }}
              />
              <Space style={{ marginTop: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={handleBackToSettings}>
                  契約プラン設定に戻る
                </Button>
              </Space>
            </div>

            <Alert
              type="info"
              showIcon
              message="お支払いに関するご案内"
              description={
                <Space direction="vertical" size={4}>
                  <Text>・初回利用月は利用開始日から当月末日までの日割り計算による料金を請求いたします。</Text>
                  <Text>・２ヶ月目以降は毎月末日を請求基準日とし、翌月分の月額料金全額を請求いたします。</Text>
                  <Text>・振込手数料は貴社にてご負担ください。</Text>
                  <Text>・入金確認まで最大で5営業日ほどお時間をいただく場合があります。</Text>
                  <Text>・入金が確認できると、担当者よりメールでご連絡いたします（メール受信には最大3分程度かかる場合があります）。</Text>
                </Space>
              }
            />
            {payload.savedAt && (
              <Text type="secondary">
                最終更新: {formatDate(payload.savedAt)}
              </Text>
            )}
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default BankTransferConfirmationPage;
